import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@ui/lib/utils';
import { forceReflow } from '../utils/animation-utils';
import '../components/engagement.css';

interface AnimatedShadowWrapperProps {
  children: React.ReactNode;
  showAnimation: boolean;
  hasBorder?: boolean;
  className?: string;
  animationKey?: number;
}

export const AnimatedShadowWrapper: React.FC<AnimatedShadowWrapperProps> = ({
  children,
  showAnimation,
  hasBorder = false,
  className = '',
  animationKey = 0,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  const animationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Small delay to ensure styles are properly computed
    const timer = setTimeout(() => {
      setIsMounted(true);
    }, 50);

    return () => {
      clearTimeout(timer);
      setIsMounted(false);
    };
  }, []);

  // Force animation reset when animation key changes
  useEffect(() => {
    if (showAnimation && animationKey > 0) {
      // Force a re-render to restart animation
      setForceUpdate((prev) => prev + 1);

      // Ensure the element is visible and ready for animation
      if (animationRef.current) {
        // Force a reflow to ensure animation restarts
        forceReflow(animationRef.current);
      }
    }
  }, [showAnimation, animationKey]);

  // Force animation reset when route changes
  useEffect(() => {
    const handleRouteChange = () => {
      setIsMounted(false);
      setTimeout(() => setIsMounted(true), 50);
    };

    window.addEventListener('popstate', handleRouteChange);
    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  return (
    <motion.div
      ref={animationRef}
      className={cn(
        className,
        isMounted && showAnimation && hasBorder && 'circular-gradient-border',
      )}
      key={`gradient-${isMounted}-${forceUpdate}-${animationKey}`}
      initial={{ opacity: 0.99 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 5, repeat: showAnimation ? Infinity : 0 }}>
      {children}
    </motion.div>
  );
};
